import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyVisits, getUsers, updateAllowedViewers, changePassword, updateProfile } from '../services/api';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedViewers, setSelectedViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Notifications state
  const [email, setEmail] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setEmailNotifications(user.emailNotifications || false);
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [visitsRes, usersRes] = await Promise.all([
        getMyVisits(),
        getUsers()
      ]);
      setVisits(visitsRes.data);
      setUsers(usersRes.data);

      // If user has no allowedViewers set (empty array), default to all users selected
      const currentViewers = user.allowedViewers?.map(v => v._id || v) || [];
      if (currentViewers.length === 0) {
        setSelectedViewers(usersRes.data.map(u => u._id));
      } else {
        setSelectedViewers(currentViewers);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewerToggle = (userId) => {
    setSelectedViewers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleSaveViewers = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await updateAllowedViewers(selectedViewers);
      updateUser(response.data);
      setMessage('Preferenze salvate con successo');
    } catch (error) {
      setMessage('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    if (newPassword.length < 6) {
      setPasswordMessage('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage('Le password non corrispondono');
      return;
    }

    setSavingPassword(true);
    try {
      await changePassword(newPassword);
      setPasswordMessage('Password aggiornata con successo');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordMessage(error.response?.data?.message || 'Errore nel cambio password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setNotifMessage('');
    setSavingNotif(true);

    try {
      const response = await updateProfile({ email, emailNotifications });
      updateUser(response.data);
      setNotifMessage('Preferenze notifiche salvate');
    } catch (error) {
      setNotifMessage('Errore nel salvataggio');
    } finally {
      setSavingNotif(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  if (loading) return <div className="loading">Caricamento...</div>;

  return (
    <div className="page-container">
      <h1>Profilo</h1>

      <section className="section">
        <h2>Le mie visite</h2>
        {visits.length === 0 ? (
          <p className="empty-state">Non hai ancora effettuato visite</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Persona visitata</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={visit._id}>
                    <td data-label="Data">{formatDate(visit.date)}</td>
                    <td data-label="Persona visitata">{visit.targetId?.name || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="section">
        <h2>Cambia password</h2>
        <form onSubmit={handleChangePassword} className="profile-form">
          <div className="form-group">
            <label htmlFor="newPassword">Nuova password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimo 6 caratteri"
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Conferma password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingPassword}>
            {savingPassword ? 'Salvataggio...' : 'Aggiorna password'}
          </button>
          {passwordMessage && (
            <p className={passwordMessage.includes('Errore') || passwordMessage.includes('corrispondono') || passwordMessage.includes('almeno') ? 'error-message' : 'success-message'}>
              {passwordMessage}
            </p>
          )}
        </form>
      </section>

      <section className="section">
        <h2>Notifiche email</h2>
        <p className="section-description">
          Ricevi una email quando qualcuno crea una nuova proposta di visita.
        </p>
        <form onSubmit={handleSaveNotifications} className="profile-form">
          <div className="form-group">
            <label htmlFor="email">Indirizzo email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="La tua email"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
              />
              <span>Ricevi email per nuove proposte</span>
            </label>
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingNotif}>
            {savingNotif ? 'Salvataggio...' : 'Salva preferenze'}
          </button>
          {notifMessage && (
            <p className={notifMessage.includes('Errore') ? 'error-message' : 'success-message'}>
              {notifMessage}
            </p>
          )}
        </form>
      </section>

      <section className="section">
        <h2>Chi puo vedere le mie proposte</h2>
        <p className="section-description">
          Seleziona gli utenti che possono visualizzare e partecipare alle tue proposte.
          Se non selezioni nessuno, tutti potranno vederle.
        </p>

        {users.length === 0 ? (
          <p className="empty-state">Nessun altro utente presente</p>
        ) : (
          <>
            <div className="checkbox-list">
              {users.map((u) => (
                <label key={u._id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedViewers.includes(u._id)}
                    onChange={() => handleViewerToggle(u._id)}
                  />
                  <span>{u.username}</span>
                </label>
              ))}
            </div>

            <button
              onClick={handleSaveViewers}
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Salvataggio...' : 'Salva preferenze'}
            </button>

            {message && (
              <p className={message.includes('Errore') ? 'error-message' : 'success-message'}>
                {message}
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default Profile;
